import React, { useEffect, useMemo, useState } from "react";
import { resolveEventMediaUrl, isVideoUrl as isVideo } from "../../lib/eventMedia";

function parseEventDate(e) {
  const raw = e?.date || e?.starts_at || e?.startsAt || e?.event_date || e?.eventDate || "";
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfWeek(d) {
  // Monday as start
  const x = new Date(d);
  const day = x.getDay(); // 0 Sun
  const diff = (day === 0 ? -6 : 1) - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfWeek(d) {
  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 7);
  e.setMilliseconds(e.getMilliseconds() - 1);
  return e;
}

function pickFlyerUrl(ev) {
  // Support multiple schemas:
  // - flyerData: [url]
  // - flyer_urls: [url]
  // - flyer_url: url
  // - flyer: url
  return (
    (Array.isArray(ev?.flyerData) && ev.flyerData[0]) ||
    (Array.isArray(ev?.flyer_urls) && ev.flyer_urls[0]) ||
    (Array.isArray(ev?.flyers) && ev.flyers[0]) ||
    ev?.flyer_url ||
    ev?.flyer ||
    ""
  );
}

// media resolving handled via src/lib/eventMedia

export default function HomeEventsCarousel({
  events = [],
  title = "Community Events",
  weekOnly = false,
  onViewAll,
}) {
  const list = Array.isArray(events) ? events : [];

  const filtered = useMemo(() => {
    const base = list
      .map((e) => ({ ...e, __dt: parseEventDate(e) }))
      .filter((e) => e.title || e.description || pickFlyerUrl(e))
      // upcoming first (soonest), then past
      .sort((a, b) => {
        const at = a.__dt ? a.__dt.getTime() : 0;
        const bt = b.__dt ? b.__dt.getTime() : 0;
        const now = Date.now();
        const aUp = at >= now;
        const bUp = bt >= now;
        if (aUp && !bUp) return -1;
        if (!aUp && bUp) return 1;
        if (aUp && bUp) return at - bt;
        return bt - at;
      });

    if (!weekOnly) return base;

    const now = new Date();
    const s = startOfWeek(now);
    const e = endOfWeek(now);
    return base.filter((x) => x.__dt && x.__dt >= s && x.__dt <= e);
  }, [list, weekOnly]);

  const [idx, setIdx] = useState(0);
  const [zoomSrc, setZoomSrc] = useState("");
  const [flyerSrc, setFlyerSrc] = useState("");

  useEffect(() => {
    // Prefer starting on an event that has a flyer/video so the homepage always shows media.
    const firstWithFlyer = filtered.findIndex((e) => !!pickFlyerUrl(e));
    setIdx(firstWithFlyer >= 0 ? firstWithFlyer : 0);
  }, [filtered.length, weekOnly]);

  const current = filtered[idx] || null;

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!current) {
        if (alive) setFlyerSrc("");
        return;
      }
      const raw = pickFlyerUrl(current);
      const url = await resolveEventMediaUrl(raw, current?.id);
      if (alive) setFlyerSrc(url || "");
    })();
    return () => {
      alive = false;
    };
  }, [
    current?.id,
    Array.isArray(current?.flyers) ? current.flyers.join("|") : current?.flyers,
    Array.isArray(current?.flyer_urls) ? current.flyer_urls.join("|") : current?.flyer_urls,
    Array.isArray(current?.flyerData) ? current.flyerData.join("|") : current?.flyerData,
    current?.flyer_url,
    current?.flyer,
  ]);

  const flyerIsVideo = useMemo(() => isVideo(flyerSrc), [flyerSrc]);

  const count = filtered.length;
  const prev = () => setIdx((v) => (count ? (v - 1 + count) % count : 0));
  const next = () => setIdx((v) => (count ? (v + 1) % count : 0));

  // Auto-advance (modern carousel feel). Pauses when zoom modal is open.
  useEffect(() => {
    if (count <= 1) return;
    if (zoomSrc) return;
    const t = setInterval(() => {
      setIdx((v) => (count ? (v + 1) % count : 0));
    }, 6000);
    return () => clearInterval(t);
  }, [count, zoomSrc]);

  return (
    <div className="card--section">
      <div className="section__header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div className="section__title">{title}</div>
          <div className="section__subtitle" style={{ opacity: 0.85 }}>
            {weekOnly ? "This week" : "Latest"} · {count} {count === 1 ? "event" : "events"}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {typeof onViewAll === "function" && (
            <button className="btn btn--ghost" onClick={onViewAll} type="button">
              View all events
            </button>
          )}
          {count > 1 && (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button
                className="pill"
                type="button"
                onClick={prev}
                aria-label="Previous event"
                style={{ width: 44, height: 44, padding: 0, flex: "0 0 auto", borderRadius: 999 }}
              >
                ‹
              </button>
              <button
                className="pill"
                type="button"
                onClick={next}
                aria-label="Next event"
                style={{ width: 44, height: 44, padding: 0, flex: "0 0 auto", borderRadius: 999 }}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1.2fr", gap: 16, marginTop: 12 }}>
        <div className="card--panel" style={{ minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {flyerSrc ? (
            <button
              type="button"
              onClick={() => setZoomSrc(flyerSrc)}
              style={{ background: "transparent", border: "none", padding: 0, cursor: "zoom-in", width: "100%" }}
              aria-label="Open flyer"
            >
              {flyerIsVideo ? (
                <video
                  src={flyerSrc}
                  controls
                  style={{ width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 16 }}
                />
              ) : (
                <img
                  src={flyerSrc}
                  alt={current?.title ? `${current.title} flyer` : "Event flyer"}
                  style={{ width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 16 }}
                  onError={() => setFlyerSrc("")}
                />
              )}
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8, textAlign: "left" }}>Click flyer to zoom</div>
            </button>
          ) : (
            <div style={{ opacity: 0.75, textAlign: "center" }}>
              <div style={{ fontWeight: 700 }}>No flyer uploaded</div>
              <div style={{ fontSize: 12 }}>Publish events with a flyer for best visibility.</div>
            </div>
          )}
        </div>

        <div className="card--panel" style={{ minHeight: 260 }}>
          {current ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{current.title || "Untitled event"}</div>
              {current.__dt && (
                <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
                  {current.__dt.toLocaleString()}
                </div>
              )}
              {current.location && <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 10 }}>{current.location}</div>}
              {current.description && <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.9 }}>{current.description}</div>}
              {current.link && (
                <div style={{ marginTop: 12 }}>
                  <a className="link" href={current.link} target="_blank" rel="noreferrer">
                    Open link
                  </a>
                </div>
              )}
            </>
          ) : (
            <div style={{ opacity: 0.8 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>No events yet</div>
              <div style={{ fontSize: 12 }}>Publish an event to see it here.</div>
            </div>
          )}
        </div>
      </div>

      {/* Dots */}
      {count > 1 && (
        <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 8 }}>
          {filtered.slice(0, 10).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Go to event ${i + 1}`}
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,.6)",
                background: i === idx ? "rgba(52,211,153,.9)" : "rgba(15,23,42,.6)",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      )}

      {zoomSrc && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setZoomSrc("")}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1100px, 95vw)",
              maxHeight: "92vh",
              overflow: "auto",
              borderRadius: 18,
            }}
          >
            {isVideo(zoomSrc) ? (
              <video
                src={zoomSrc}
                controls
                style={{
                  display: "block",
                  margin: "0 auto",
                  width: "min(1100px, 95vw)",
                  maxWidth: "95vw",
                  height: "auto",
                  maxHeight: "85vh",
                  borderRadius: 18,
                  background: "#000",
                }}
              />
            ) : (
              <img
                src={zoomSrc}
                alt="Event flyer"
                style={{
                  display: "block",
                  margin: "0 auto",
                  width: "auto",
                  maxWidth: "95vw",
                  height: "auto",
                  maxHeight: "85vh",
                  objectFit: "contain",
                  borderRadius: 18,
                }}
              />
            )}
            <div style={{ marginTop: 10, textAlign: "center" }}>
              <button className="btn" type="button" onClick={() => setZoomSrc("")}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}









